import axios from 'axios'
import store from '../index'
import { make } from 'vuex-pathify'
import { getQueryFromFilter, showError, showSuccess } from '@/util/helpers'
import { formatDate } from '@/util/formatters'
import { PERMISSIONS } from '@/enums/permissions'

const state = {
  customer: {
    addresses: [],
    contacts: [],
    equipments: [],
  },

  customers: {
    data: [],
    count: 0,
    limit: 50,
  },

  filter: {
    page: 1,
    itemsPerPage: 25,
    sortBy: ['name'],
    sortDesc: [false],
  },

  customerStats: {
    data: {},
    dialog: false,
  },

  type: 'add',
  tab: '',
  loading: false,
  dialog: false,
  dialogUpdate: false,
}

const mutations = make.mutations(state)

const actions = {
  add: ({ state }) => {
    state.customer = {
      addresses: [],
      contacts: [],
      equipments: [],
      active: true,
      companyId: store.get('authStore@currentCompany').id,
      userId: store.get('authStore@currentUser').id,
    }
    state.type = 'add'
    state.dialog = true
    state.tab = ''
  },

  edit: ({ state }, { id, tab }) => {
    state.loading = true
    store.dispatch('customerStore/getById', id)
      .then(res => {
        state.customer = res.data

        state.type = 'edit'
        state.dialog = true
        state.tab = tab || ''

        // format data
        state.customer.birthDate = state.customer.birthDate ? formatDate(new Date(res.data.birthDate).toISOString().substr(0, 10)) : null
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  view: ({ state }, id) => {
    state.loading = true
    store.dispatch('customerStore/getById', id)
      .then(res => {
        state.customer = res.data

        state.type = 'view'
        state.dialog = true

        // format data
        state.customer.birthDate = state.customer.birthDate ? formatDate(new Date(res.data.birthDate).toISOString().substr(0, 10)) : null
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  getCustomerStats: ({ state }, id) =>
    axios.get(`${process.env.VUE_APP_API_URL}/customer-stats/${id}`)
      .then(res => {
        state.customerStats.data = res.data

        // format data
        state.customerStats.data.lastRequest = res.data.lastRequest
          ? formatDate(new Date(res.data.lastRequest).toISOString().substr(0, 10))
          : null
        state.customerStats.data.mostRequestedProduct = res.data.mostRequestedProduct
          ? `${res.data.mostRequestedProduct.name} - Quantidade vendida: ${res.data.mostRequestedProduct.sum}`
          : 'Nenhum produto encontrado'

        return res.data
      })
      .catch(showError),

  showCustomerStats: ({ state }, id) => {
    state.loading = true
    store.dispatch('customerStore/getCustomerStats', id)
      .then(_ => {
        state.customerStats.dialog = true
      })
      .finally(() => (state.loading = false))
  },

  save: ({ state }, customer) => {
    const id = customer.id
    const method = id ? 'put' : 'post'

    const crmPending = store.state.crmOpportunityStore.formalizing
    const crmEpoch = store.state.crmOpportunityStore.epoch
    const customerAtStart = state.customer
    state.loading = true
    const payload = { ...customer }
    delete payload.imgUrl
    return axios[method](`${process.env.VUE_APP_API_URL}/customer/${id || ''}`, payload)
      .then((res) => {
        if (state.customer !== customerAtStart || (crmPending && (crmEpoch !== store.state.crmOpportunityStore.epoch || store.state.crmOpportunityStore.formalizing !== crmPending))) {
          return
        }
        if (!id && crmPending && crmEpoch === store.state.crmOpportunityStore.epoch) {
          store.dispatch('crmOpportunityStore/customerCreated', { id: res.data.id, pending: crmPending, customer: { name: payload.name, cpfCnpj: payload.cpfCnpj } })
        }
        state.dialog = false
        store.dispatch('customerStore/get')
        showSuccess('Cliente salvo com sucesso!')
      })
      .catch(showError)
      .finally(() => {
        if (state.customer === customerAtStart && (!crmPending || crmEpoch === store.state.crmOpportunityStore.epoch)) {
          state.loading = false
        }
      })
  },

  get: async ({ state }, filter = state.filter) => {
    state.loading = true
    return axios.get(`${process.env.VUE_APP_API_URL}/customer?${getQueryFromFilter(filter)}`)
      .then(res => {
        state.customers = res.data
        return res.data
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  getById: (context, id) => axios.get(`${process.env.VUE_APP_API_URL}/customer/${id}`),

  getByCpfCnpj: async ({ state }, cpfCnpj) => {
    state.loading = true
    return axios.get(`${process.env.VUE_APP_API_URL}/customer?${getQueryFromFilter({ cpfCnpj })}`)
      .then(res => {
        return res.data
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  getCnpjInfo: async (_, { cnpj }) => {
    state.loading = true
    return axios.post(`${process.env.VUE_APP_API_URL}/cnpj/info`, { cnpj })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  remove: ({ state }, id) => {
    state.loading = true
    return axios.delete(`${process.env.VUE_APP_API_URL}/customer/${id}`)
      .then(_ => {
        store.dispatch('customerStore/get')
        showSuccess('Cliente excluído com sucesso!')
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  getVehicles: async ({ state }, filter) => {
    state.loading = true
    return axios.get(`${process.env.VUE_APP_API_URL}/customer-vehicles/${filter.customerId}?${getQueryFromFilter(filter)}`)
      .catch(showError)
      .finally(() => (state.loading = false))
  },
}

const getters = {
  getById: state => id => {
    return state.customers.data.find((u) => String(u.id) === String(id))
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
